import { FILTER_LABEL_CLASS } from '@/components/admin/versioned/filter-field';
import { useIsVitrine } from '@/lib/use-instance';
import type { VersionedWording } from '@/lib/versioned-labels';
import type { VersionedFilters } from '@matter_extractor/shared';
import { Checkbox, TextField } from '@pms-digital/design-system';
import type { ReactNode } from 'react';

/**
 * « Plus de filtres » - the dimensions that answer a question somebody arrived
 * with (refonte modulaire §4).
 *
 * The object's OWN filters, then four common controls and four boxes:
 *
 *   - the **object's own slot**, first, because it is what somebody opened the
 *     panel for: the number of examples for a criterion (§6), the presentation
 *     and the cran for a persona (§10);
 *   - the two **periods**, created and last version, both bounds inclusive;
 *   - **« Afficher les retirés »**, which is the only box that ADDS rows rather
 *     than removing them - hence the count beside it, so the eye knows there is
 *     something to reveal before ticking;
 *   - **« Utilisé nulle part »**, **« Brouillon en attente »** and
 *     **« Développement en avance sur la production »**, the three questions a
 *     card cannot be scanned for: each needs a comparison, and
 *     `versionedCardShape` carries the two computed ones server-side for
 *     exactly that reason.
 *
 * The panel sits on `gray-50` and not on white, because it is a second surface
 * over the page's ground; the charte allows two levels of white and no more,
 * and `gray-200` or darker is never a surface at all.
 *
 * The object's filters are a `ReactNode` and not a description the layer would
 * render: they are form controls, and a select, a slider and a pair of radios
 * do not share a shape worth naming. They are laid out INSIDE this panel's row
 * so they align with the periods beside them - which is why the slot receives
 * nothing and simply sits first.
 */

export interface FilterPanelProps<Filters extends VersionedFilters> {
  wording: VersionedWording;
  filters: Filters;
  /** The object's own filter controls, drawn at the head of the row. */
  ownFilters: ReactNode;
  /** How many entities are retired - the « (1) » beside the box. */
  retiredCount: number;
  onChange: (patch: Partial<Filters>) => void;
  onReset: () => void;
  /** Greyed out when nothing is armed, so the link never promises a no-op. */
  resetDisabled: boolean;
}

/** One checkbox: the package `Checkbox`, a real `<input type=checkbox>` with its label. */
function FilterBox({
  label,
  checked,
  onChange,
  testId,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  testId: string;
  hint?: ReactNode;
}) {
  // The hint (« (1) ») stays INSIDE the label, as it was: it is part of the box's name.
  return (
    <Checkbox
      data-testid={testId}
      checked={checked}
      onCheckedChange={onChange}
      label={
        hint === undefined ? (
          label
        ) : (
          <>
            {label} {hint}
          </>
        )
      }
    />
  );
}

/**
 * One inclusive period: « du ... au ... », either bound optional. Two package
 * `TextField`s of `type="date"`, named « du » and « au » as the native pair was.
 */
function PeriodField({
  label,
  from,
  to,
  onFrom,
  onTo,
  testId,
}: {
  label: string;
  from: string | null;
  to: string | null;
  onFrom: (next: string | null) => void;
  onTo: (next: string | null) => void;
  testId: string;
}) {
  return (
    <div className="flex max-w-full flex-col gap-1.5">
      <span className={FILTER_LABEL_CLASS}>{label}</span>
      {/* « du [date] au [date] » on one row, as before: the package fields keep « du » and « au »
          as their names, and the visible words sit beside them. */}
      <div className="flex flex-wrap items-center gap-2">
        <span aria-hidden="true" className="text-gray-700 text-small">
          du
        </span>
        <div className="w-40">
          <TextField
            snug
            label="du"
            hideLabel
            data-testid={`${testId}-du`}
            type="date"
            value={from ?? ''}
            onChange={(event) =>
              onFrom(event.currentTarget.value === '' ? null : event.currentTarget.value)
            }
          />
        </div>
        <span aria-hidden="true" className="text-gray-700 text-small">
          au
        </span>
        <div className="w-40">
          <TextField
            snug
            label="au"
            hideLabel
            data-testid={`${testId}-au`}
            type="date"
            value={to ?? ''}
            onChange={(event) =>
              onTo(event.currentTarget.value === '' ? null : event.currentTarget.value)
            }
          />
        </div>
      </div>
    </div>
  );
}

export function FilterPanel<Filters extends VersionedFilters>({
  wording,
  filters,
  ownFilters,
  retiredCount,
  onChange,
  onReset,
  resetDisabled,
}: FilterPanelProps<Filters>) {
  /*
    Every patch below is a slice of the COMMON filters, which every object's
    type carries - but TypeScript cannot see that through an unresolved type
    parameter, so each is widened once. The cast pays for the generic and for
    nothing an object could disagree about.
  */
  const patch = (next: Partial<VersionedFilters>) => onChange(next as Partial<Filters>);
  // Two filters of the atelier's drafts and pointers: the vitrine has neither.
  const vitrine = useIsVitrine();
  // An object with one side only (npc.md N5) is cited by nothing and has no production to be
  // ahead of: the two boxes would answer a question it cannot ask.
  const oneSided = wording.developmentOnly === true;

  return (
    <div
      data-testid={`${wording.list}-filtres-panneau`}
      className="flex flex-col gap-4 rounded-md border border-gray-100 bg-gray-50 p-5"
    >
      <div className="flex flex-wrap items-end gap-3">
        {ownFilters}

        <PeriodField
          label="Créé"
          testId={`${wording.entity}-filtre-cree`}
          from={filters.createdFrom}
          to={filters.createdTo}
          onFrom={(createdFrom) => patch({ createdFrom })}
          onTo={(createdTo) => patch({ createdTo })}
        />

        <PeriodField
          label="Dernière version"
          testId={`${wording.entity}-filtre-version`}
          from={filters.versionFrom}
          to={filters.versionTo}
          onFrom={(versionFrom) => patch({ versionFrom })}
          onTo={(versionTo) => patch({ versionTo })}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-6">
          <FilterBox
            label="Afficher les retirés"
            testId={`${wording.entity}-filtre-retires`}
            checked={filters.showRetired}
            onChange={(showRetired) => patch({ showRetired })}
            hint={
              retiredCount === 0 ? undefined : (
                <span className="text-gray-500">({retiredCount})</span>
              )
            }
          />
          {oneSided ? null : (
            <FilterBox
              label="Utilisé nulle part"
              testId={`${wording.entity}-filtre-inutilise`}
              checked={filters.unusedOnly}
              onChange={(unusedOnly) => patch({ unusedOnly })}
            />
          )}
          {vitrine ? null : (
            <>
              <FilterBox
                label="Brouillon en attente"
                testId={`${wording.entity}-filtre-brouillon`}
                checked={filters.pendingDraftOnly}
                onChange={(pendingDraftOnly) => patch({ pendingDraftOnly })}
              />
              {oneSided ? null : (
                <FilterBox
                  label="Latest dev en avance sur latest prod"
                  testId={`${wording.entity}-filtre-en-avance`}
                  checked={filters.developmentAheadOnly}
                  onChange={(developmentAheadOnly) => patch({ developmentAheadOnly })}
                />
              )}
            </>
          )}
        </div>
        <button
          type="button"
          data-testid={`${wording.list}-reinitialiser`}
          disabled={resetDisabled}
          onClick={onReset}
          className="cursor-pointer whitespace-nowrap text-bleu-nuit text-small underline underline-offset-2 hover:text-bleu-nuit-deep disabled:cursor-not-allowed disabled:text-gray-500 disabled:no-underline"
        >
          Réinitialiser les filtres
        </button>
      </div>
    </div>
  );
}
